import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Light HTML normalizer for journal content.
 * Cleans common patterns that look fine in the editor but read poorly published:
 *   - Strips leading <h1> that duplicates the post title (the page already
 *     renders the title as its own <h1>).
 *   - Collapses runs of empty <p></p> spacer paragraphs (CSS margin handles
 *     real spacing — empty <p> elements just bloat the markup and break flow).
 *   - Unwraps <strong>/<b> wrappers that sit *inside* a heading — they make
 *     the heading look "extra bold" relative to other articles and break visual
 *     consistency across the journal.
 *   - Strips any inline `style=` attributes carried in from external sources.
 */
function normalizeContent(html, title) {
  if (!html) return '';
  let out = html;
  // Strip leading <h1>...</h1> if it (loosely) matches the post title
  out = out.replace(/^\s*<h1[^>]*>([\s\S]*?)<\/h1>/i, (full, inner) => {
    const innerText = inner.replace(/<[^>]+>/g, '').trim();
    if (title && innerText && innerText.toLowerCase() === title.toLowerCase()) return '';
    return full;
  });
  // Unwrap <strong>/<b> inside any heading tag so weight is consistent across articles
  out = out.replace(/<(h[1-6])([^>]*)>([\s\S]*?)<\/\1>/gi, (full, tag, attrs, inner) => {
    const cleaned = inner
      .replace(/<\s*(strong|b)([^>]*)>([\s\S]*?)<\s*\/\s*\1\s*>/gi, '$3')
      .trim();
    return `<${tag}${attrs}>${cleaned}</${tag}>`;
  });
  // Strip inline style attributes (article-level safety; CSS class governs styling)
  out = out.replace(/\sstyle\s*=\s*"[^"]*"/gi, '');
  out = out.replace(/\sstyle\s*=\s*'[^']*'/gi, '');
  // Collapse empty paragraphs (and ones containing only <br> or whitespace)
  out = out.replace(/<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, '');
  // Collapse runs of duplicate blank-paragraph residue
  out = out.replace(/(<p[^>]*>\s*<\/p>\s*){2,}/gi, '');
  return out.trim();
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/cms/journal/slug/${slug}`);
        setPost(res.data);
        // Related entries — silently ignore if endpoint or data is empty
        try {
          const relRes = await axios.get(`${API}/api/cms/journal/slug/${slug}/related?limit=3`);
          setRelated(Array.isArray(relRes.data) ? relRes.data : []);
        } catch { setRelated([]); }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center"><p className="text-sm text-[#999]">Loading...</p></div>;
  if (notFound) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center flex-col gap-4">
      <p className="font-serif text-xl text-[#1A1A1A]">Entry not found</p>
      <Link to="/blog" className="text-sm text-[#6B6B6B] underline">Back to The Journal</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="blog-post-page">
      {/* Cover Image */}
      {post.cover_image_url && (
        <div className="w-full h-[50vh] overflow-hidden">
          <img src={post.cover_image_url.startsWith('http') ? post.cover_image_url : `${API}${post.cover_image_url}`} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <article className="max-w-2xl mx-auto px-6 py-20 sm:py-24">
        <Link to="/blog" className="text-[10px] uppercase tracking-[0.22em] text-[#999] hover:text-[#1A1A1A] transition-colors">
          The Journal
        </Link>

        <h1 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-8 mb-3 leading-tight">{post.title}</h1>
        {post.subtitle && <p className="text-base text-[#6B6B6B] mb-6 italic font-serif leading-relaxed">{post.subtitle}</p>}

        <div className="flex items-center gap-3 text-xs text-[#999] mb-6 flex-wrap">
          {post.category && <span className="px-2 py-0.5 border border-[#E8E4DC] rounded-full">{post.category}</span>}
          {post.destination_tag && <span>{post.destination_tag}</span>}
          {post.published_at && <span>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>}
        </div>

        {/* ── Tag chips — clickable, filter The Journal ─────────────────── */}
        {Array.isArray(post.tags) && post.tags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-8" data-testid="blog-post-tags">
            {post.tags.map((tag) => (
              <Link
                key={tag}
                to={`/blog?tag=${encodeURIComponent(tag)}`}
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] hover:text-[#C9A961] transition-colors px-2 py-1 rounded-full border border-[#E8E4DC] hover:border-[#C9A961]/60"
                data-testid={`blog-post-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}

        {/* ── Writer credit (luxe editorial signature) ─────────────────── */}
        {post.author_name && (
          <div className="flex items-center gap-3 mb-12" data-testid="blog-author">
            <div className="size-9 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/30 flex items-center justify-center font-serif text-[14px] text-[#C9A961]">
              {post.author_name.split(' ').filter(Boolean).slice(0, 2).map((n) => n[0]).join('').toUpperCase()}
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#999]">Writer</p>
              <p className="font-serif text-[15px] text-[#1A1A1A] leading-tight">
                {post.author_name}
                {post.author_role && (
                  <span className="text-[#6B6B6B] font-accent italic text-[13px] ml-2">· {post.author_role}</span>
                )}
              </p>
            </div>
          </div>
        )}

        <div
          className="vt-journal-prose prose prose-neutral max-w-none text-[#1A1A1A] text-[16px] leading-[1.75]
            prose-headings:font-serif prose-headings:text-[#1A1A1A] prose-headings:font-normal
            prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-5 prose-h2:leading-tight
            prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-3
            prose-p:text-[#1A1A1A] prose-p:my-5 prose-p:leading-[1.75]
            prose-a:text-[#1A1A1A] prose-a:underline prose-a:decoration-[#C9A961]/40 prose-a:underline-offset-4 hover:prose-a:decoration-[#C9A961]
            prose-strong:text-[#1A1A1A] prose-strong:font-medium
            prose-blockquote:border-l-2 prose-blockquote:border-[#C9A961] prose-blockquote:pl-6 prose-blockquote:py-2 prose-blockquote:italic prose-blockquote:font-serif prose-blockquote:text-xl prose-blockquote:text-[#1A1A1A] prose-blockquote:my-10 prose-blockquote:leading-snug
            prose-ul:my-6 prose-ol:my-6 prose-li:my-1.5
            prose-img:rounded-lg prose-img:my-10 prose-img:w-full"
          dangerouslySetInnerHTML={{ __html: normalizeContent(post.content, post.title) }}
          data-testid="blog-post-content"
        />

        <div className="mt-16 pt-8 border-t border-[#E8E4DC]">
          <Link to="/blog" className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors">
            Back to The Journal
          </Link>
        </div>
      </article>

      {/* ── Related journals ─────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="bg-white border-t border-[#E8E4DC]" data-testid="related-journals">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-xs uppercase tracking-[0.18em] text-[#999] mb-1">Continue reading</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mb-10">More from The Journal</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((entry) => (
                <Link
                  key={entry.id}
                  to={`/blog/${entry.slug}`}
                  className="group block"
                  data-testid={`related-journal-${entry.id}`}
                >
                  {entry.cover_image_url && (
                    <div className="aspect-[4/3] overflow-hidden rounded-lg mb-4 bg-[#F0EDE6]">
                      <img
                        src={entry.cover_image_url.startsWith('http') ? entry.cover_image_url : `${API}${entry.cover_image_url}`}
                        alt={entry.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#999] mb-2">
                    {entry.category && <span>{entry.category}</span>}
                    {entry.category && entry.destination_tag && <span>·</span>}
                    {entry.destination_tag && <span>{entry.destination_tag}</span>}
                  </div>
                  <h3 className="font-serif text-lg text-[#1A1A1A] leading-tight group-hover:text-[#C9A961] transition-colors">
                    {entry.title}
                  </h3>
                  {entry.subtitle && (
                    <p className="mt-2 text-sm text-[#6B6B6B] line-clamp-2">{entry.subtitle}</p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
